<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import CampaignsAPI from 'dashboard/api/campaigns';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import WhatsAppCampaignWorkspace from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignWorkspace.vue';
import WhatsAppPhonePreview from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppPhonePreview.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const labels = useMapGetter('labels/getLabels');
const lists = useMapGetter('contactLists/getLists');

const campaign = ref(null);
const recipients = ref([]);
const isLoading = ref(true);
const statusFilter = ref('');
const activeTab = ref('results');

const tabs = computed(() => [
  { label: t('CAMPAIGN.WHATSAPP.SHOW.TABS.RESULTS'), value: 'results' },
  { label: t('CAMPAIGN.WHATSAPP.SHOW.TABS.INFO'), value: 'info' },
]);

const activeTabIndex = computed(() =>
  Math.max(
    tabs.value.findIndex(tab => tab.value === activeTab.value),
    0
  )
);

const onTabChanged = tab => {
  activeTab.value = tab.value;
};

const recipientStatuses = [
  'pending',
  'sending',
  'sent',
  'delivered',
  'read',
  'failed',
  'skipped',
  'cancelled',
];

const load = async () => {
  isLoading.value = true;
  campaign.value = null;
  recipients.value = [];
  try {
    const params = statusFilter.value ? { status: statusFilter.value } : {};
    const [campaignResponse, recipientsResponse] = await Promise.all([
      CampaignsAPI.show(route.params.campaignId),
      CampaignsAPI.recipients(route.params.campaignId, params),
    ]);
    campaign.value = campaignResponse.data;
    recipients.value = recipientsResponse.data.payload || [];
  } finally {
    isLoading.value = false;
  }
};

const runAction = async action => {
  try {
    const response = await CampaignsAPI[action](route.params.campaignId);
    campaign.value = response.data;
    await load();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE')
    );
  }
};

const templateStatus = computed(() =>
  String(campaign.value?.template_params?.status || '').toUpperCase()
);
const templateRejectedReason = computed(
  () => campaign.value?.template_params?.rejected_reason
);
const templateParams = computed(() => campaign.value?.template_params || {});
const audience = computed(() =>
  campaign.value?.audience && !Array.isArray(campaign.value.audience)
    ? campaign.value.audience
    : {}
);

const namesFor = (ids, records, key) => {
  const wanted = new Set((ids || []).map(id => Number(id)));
  return (records || [])
    .filter(item => wanted.has(Number(item.id)))
    .map(item => item[key])
    .filter(Boolean);
};

const includedLists = computed(() =>
  namesFor(audience.value.include_list_ids, lists.value, 'name')
);
const excludedLists = computed(() =>
  namesFor(audience.value.exclude_list_ids, lists.value, 'name')
);
const includedLabels = computed(() =>
  namesFor(audience.value.include_label_ids, labels.value, 'title')
);
const excludedLabels = computed(() =>
  namesFor(audience.value.exclude_label_ids, labels.value, 'title')
);

const categoryLabel = computed(() => {
  const category = String(templateParams.value.category || '').toUpperCase();
  if (category === 'UTILITY') {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.UTILITY');
  }
  if (category === 'MARKETING') {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.MARKETING');
  }
  return '';
});

const languageLabel = computed(() => {
  if (!templateParams.value.language) return '';
  return templateParams.value.language === 'en'
    ? t('CAMPAIGN.WHATSAPP.WORKSPACE.LANG_EN')
    : t('CAMPAIGN.WHATSAPP.WORKSPACE.LANG_ES');
});

const previewButtons = computed(() =>
  (templateParams.value.buttons || []).map(button => ({
    type: String(button.type || '').toUpperCase(),
    text: button.text || '',
  }))
);

const scheduledLabel = computed(() => {
  const unix = Number(campaign.value?.scheduled_at || 0);
  if (!unix) return '';
  const created = new Date(campaign.value?.created_at).getTime();
  if (!created || unix * 1000 - created < 120000) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(unix * 1000));
});

const statusLabel = status =>
  t(`CAMPAIGN.WHATSAPP.CARD.STATUS.${String(status || '').toUpperCase()}`);

const recipientStatusLabel = status =>
  t(
    `CAMPAIGN.WHATSAPP.SHOW.RECIPIENT_STATUS.${String(status || '').toUpperCase()}`
  );

const goBack = () => {
  router.push({
    name: 'campaigns_whatsapp_index',
    params: { accountId: route.params.accountId },
  });
};

const onUpdated = async updated => {
  campaign.value = updated;
  if (updated.campaign_status !== 'draft') await load();
};

watch(
  () => route.params.campaignId,
  (campaignId, previousId) => {
    if (!campaignId || campaignId === previousId) return;
    statusFilter.value = '';
    activeTab.value = 'results';
    load();
  }
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('contactLists/get');
  load();
});
</script>

<template>
  <section
    class="flex flex-col w-full h-full gap-6 px-6 py-6 overflow-y-auto bg-n-surface-1"
  >
    <div class="flex items-center justify-between w-full max-w-5xl mx-auto">
      <Button
        variant="faded"
        color="slate"
        icon="i-lucide-arrow-left"
        :label="t('CAMPAIGN.WHATSAPP.SHOW.BACK')"
        @click="goBack"
      />
      <div v-if="campaign" class="flex gap-2">
        <Button
          v-if="campaign.campaign_status === 'sending'"
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.WHATSAPP.SHOW.PAUSE')"
          @click="runAction('pause')"
        />
        <Button
          v-if="campaign.campaign_status === 'paused'"
          :label="t('CAMPAIGN.WHATSAPP.SHOW.RESUME')"
          @click="runAction('resume')"
        />
        <Button
          v-if="
            ['draft', 'scheduled', 'sending', 'paused'].includes(
              campaign.campaign_status
            )
          "
          variant="faded"
          color="ruby"
          :label="t('CAMPAIGN.WHATSAPP.SHOW.CANCEL')"
          @click="runAction('cancel')"
        />
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <WhatsAppCampaignWorkspace
      v-else-if="campaign?.campaign_status === 'draft'"
      :campaign="campaign"
      @updated="onUpdated"
    />

    <div
      v-else-if="campaign"
      class="flex flex-col w-full max-w-5xl gap-6 mx-auto"
    >
      <div class="flex flex-col gap-2">
        <h1 class="text-heading-1 text-n-slate-12">{{ campaign.title }}</h1>
        <span class="text-sm text-n-slate-11">{{
          statusLabel(campaign.campaign_status)
        }}</span>
        <p
          v-if="templateStatus === 'PENDING'"
          class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP.SHOW.PENDING') }}
        </p>
        <p
          v-else-if="templateStatus === 'REJECTED'"
          class="p-3 text-sm rounded-lg bg-n-ruby-3 text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP.SHOW.REJECTED') }}
          <span v-if="templateRejectedReason">{{
            templateRejectedReason
          }}</span>
        </p>
        <p
          v-else-if="
            campaign.campaign_status === 'draft' &&
            templateStatus === 'APPROVED'
          "
          class="p-3 text-sm rounded-lg bg-n-teal-3 text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP.SHOW.READY') }}
        </p>
      </div>

      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTabIndex"
        @tab-changed="onTabChanged"
      />

      <div
        v-if="activeTab === 'info'"
        class="grid items-start grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]"
      >
        <div
          class="flex flex-col gap-3 px-4 py-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
        >
          <h2 class="text-heading-3 text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.SHOW.TEMPLATE') }}
          </h2>
          <div v-if="categoryLabel" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.SHOW.CATEGORY') }}
            </span>
            <span class="text-sm text-n-slate-12">{{ categoryLabel }}</span>
          </div>
          <div v-if="languageLabel" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.SHOW.LANGUAGE') }}
            </span>
            <span class="text-sm text-n-slate-12">{{ languageLabel }}</span>
          </div>
          <div v-if="templateParams.name" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.SHOW.TEMPLATE_NAME') }}
            </span>
            <span class="text-sm text-n-slate-12">{{
              templateParams.name
            }}</span>
          </div>
          <div v-if="includedLists.length" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.SHOW.LISTS') }}
            </span>
            <span class="text-sm text-n-slate-12">{{
              includedLists.join(', ')
            }}</span>
          </div>
          <div v-if="includedLabels.length" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.SHOW.LABELS') }}
            </span>
            <span class="text-sm text-n-slate-12">{{
              includedLabels.join(', ')
            }}</span>
          </div>
          <div v-if="excludedLists.length" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.SHOW.EXCLUDE_LISTS') }}
            </span>
            <span class="text-sm text-n-slate-12">{{
              excludedLists.join(', ')
            }}</span>
          </div>
          <div v-if="excludedLabels.length" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.SHOW.EXCLUDE_LABELS') }}
            </span>
            <span class="text-sm text-n-slate-12">{{
              excludedLabels.join(', ')
            }}</span>
          </div>
          <div v-if="scheduledLabel" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.SHOW.WHEN') }}
            </span>
            <span class="text-sm text-n-slate-12">{{ scheduledLabel }}</span>
          </div>
        </div>
        <WhatsAppPhonePreview
          :header-type="templateParams.header_type || 'none'"
          :header-text="templateParams.header_text"
          :header-image="templateParams.header_media_url"
          :body="templateParams.body || campaign.message"
          :footer="templateParams.footer"
          :buttons="previewButtons"
        />
      </div>

      <div v-if="activeTab === 'results'" class="flex flex-col gap-6">
        <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div
            v-for="key in ['sent', 'delivered', 'read', 'failed']"
            :key="key"
            class="p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
          >
            <p class="text-xs text-n-slate-11">
              {{
                t(
                  `CAMPAIGN.WHATSAPP.CARD.CAMPAIGN_DETAILS.${key.toUpperCase()}`
                )
              }}
            </p>
            <p class="text-xl font-medium text-n-slate-12">
              {{ campaign.recipient_counts?.[key] || 0 }}
            </p>
          </div>
        </div>

        <div
          v-if="
            campaign.error_breakdown &&
            Object.keys(campaign.error_breakdown).length
          "
          class="flex flex-col gap-2"
        >
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.SHOW.ERRORS') }}
          </h2>
          <p
            v-for="(total, code) in campaign.error_breakdown"
            :key="code"
            class="text-sm text-n-slate-11"
          >
            {{ code || t('CAMPAIGN.WHATSAPP.SHOW.UNKNOWN_ERROR') }}: {{ total }}
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between gap-3">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.SHOW.RECIPIENTS') }}
            </h2>
            <select
              v-model="statusFilter"
              :aria-label="t('CAMPAIGN.WHATSAPP.SHOW.FILTER')"
              @change="load"
            >
              <option value="">
                {{ t('CAMPAIGN.WHATSAPP.SHOW.ALL_STATUSES') }}
              </option>
              <option
                v-for="status in recipientStatuses"
                :key="status"
                :value="status"
              >
                {{ recipientStatusLabel(status) }}
              </option>
            </select>
          </div>
          <p v-if="!recipients.length" class="text-sm text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.SHOW.EMPTY_RECIPIENTS') }}
          </p>
          <div
            v-else
            class="overflow-hidden rounded-xl outline outline-1 outline-n-container"
          >
            <div
              class="grid grid-cols-4 gap-2 px-4 py-2 text-xs font-medium text-n-slate-11 bg-n-alpha-2"
            >
              <span>{{ t('CAMPAIGN.WHATSAPP.SHOW.COLUMNS.NAME') }}</span>
              <span>{{ t('CAMPAIGN.WHATSAPP.SHOW.COLUMNS.PHONE') }}</span>
              <span>{{ t('CAMPAIGN.WHATSAPP.SHOW.COLUMNS.STATUS') }}</span>
              <span>{{ t('CAMPAIGN.WHATSAPP.SHOW.COLUMNS.ERROR') }}</span>
            </div>
            <div
              v-for="recipient in recipients"
              :key="recipient.id"
              class="grid grid-cols-4 gap-2 px-4 py-3 text-sm border-t border-n-weak text-n-slate-12"
            >
              <span class="truncate">{{ recipient.contact_name }}</span>
              <span class="truncate">{{ recipient.phone_number }}</span>
              <span>{{ recipientStatusLabel(recipient.status) }}</span>
              <span class="truncate text-n-slate-11">{{
                recipient.error_code
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
